/**
 * HitDetection - Cone-based hit detection for melee and ranged attacks.
 * Finds targets within range and facing-angle cone of the attacker.
 */
export default class HitDetection {
  /** Attack definitions per type */
  static ATTACK_DEFS = {
    light:   { range: 1.8, cone: 90,  damageMul: 1.0, knockback: 1.5, hitFrame: 2 },
    heavy:   { range: 2.2, cone: 120, damageMul: 2.0, knockback: 3.0, hitFrame: 3 },
    special: { range: 5.0, cone: 30,  damageMul: 1.5, knockback: 2.0, hitFrame: 4 }
  };

  /**
   * Find all targets hit by an attack.
   * @param {Character} attacker
   * @param {string} attackType - 'light' | 'heavy' | 'special'
   * @param {Map<string, Character>} characters - all characters in world
   * @returns {Character[]} hit targets
   */
  static findTargets(attacker, attackType, characters) {
    const def = HitDetection.ATTACK_DEFS[attackType];
    if (!def) return [];

    const hits = [];
    const ax = attacker.x;
    const az = attacker.z;
    const facingRad = (attacker.facingAngle || 0) * Math.PI / 180;
    const rangeSq = def.range * def.range;
    const halfCone = (def.cone / 2) * Math.PI / 180;

    for (const target of characters.values()) {
      // Skip self
      if (target === attacker) continue;
      // Skip dead or invulnerable
      if (!target.combatStats || target.combatStats.isDead) continue;
      if (target.combatStats.isInvulnerable) continue;
      // Faction check: players hit NPCs, NPCs hit players
      if (attacker.isNPC === target.isNPC) continue;

      const dx = target.x - ax;
      const dz = target.z - az;
      const distSq = dx * dx + dz * dz;

      if (distSq > rangeSq) continue;

      // Cone check: angle from attacker facing to target
      const angleToTarget = Math.atan2(dz, dx);
      let angleDiff = angleToTarget - facingRad;
      // Normalize to [-PI, PI]
      angleDiff = ((angleDiff + Math.PI * 3) % (Math.PI * 2)) - Math.PI;

      if (Math.abs(angleDiff) <= halfCone) {
        hits.push(target);
      }
    }
    return hits;
  }
}
